import { parseAppRelease, type AppRelease } from '@/features/app-download/app-release';

/**
 * 「要不要提示更新」的判断（CR-067）。纯函数，hook 和用例共用。
 *
 * 客户原话「release apk包增加版本检查，自动更新的功能」。
 * 比的是 **versionCode**：版本名是 git SHA，没有大小之分；versionCode 由一键部署递增（`Get-VersionCode`），
 * 覆盖安装也要求它只增不减，所以它是唯一能比的东西。
 */

/** 最多多久查一次。工地弱网，查不到就当没有，别让它每次切前台都发请求 */
export const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

/** 上次检查的时刻存这里（按 App 存，不按用户：装的是哪一版和谁登录无关） */
export const LAST_CHECK_KEY = 'construction.updateCheckedAt';

/** 关掉过的版本存这里：同一版不再打扰，出了更新的版本照旧提示 */
export const DISMISSED_KEY = 'construction.updateDismissed';

/**
 * 上次查到的清单存这里。
 *
 * **节流的是「发不发请求」，不是「记不记得有新版」**（真机实测踩过）：
 * 只记时间戳的话，查过一次之后 App 冷启动 6 小时内一律直接返回，
 * 横幅就此消失 —— 工人重启一次就再也看不到更新提示，而这功能的全部意义就是让他看得到。
 */
export const RELEASE_KEY = 'construction.updateRelease';

/** 存下来的清单可能是旧版本写的、也可能被改坏，一律按新清单的规矩重新校验 */
export function readCachedRelease(raw: string | null): AppRelease | null {
  if (!raw) return null;
  try {
    return parseAppRelease(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function dueForCheck(lastCheckedAt: number | null, now: number): boolean {
  if (lastCheckedAt === null || !Number.isFinite(lastCheckedAt)) return true;
  // 手机时钟被往前调过（打卡那边也防着这一手）：当作该查了
  if (lastCheckedAt > now) return true;
  return now - lastCheckedAt >= CHECK_INTERVAL_MS;
}

/**
 * 服务器上那份比手机上装的新，而且不是被关掉的那一版 → 提示。
 *
 * `current` 是 `App.getInfo().build`（安卓上就是 versionCode 的字符串）。读不出来（网页端、拿不到插件）
 * 一律不提示：宁可不提示，也不能给工人一个「点了装不上」的横幅。
 */
export function shouldOfferUpdate(
  current: number | null,
  release: AppRelease | null,
  dismissedVersionCode: number | null = null,
): boolean {
  if (current === null || release === null) return false;
  if (release.versionCode <= current) return false;
  return dismissedVersionCode !== release.versionCode;
}

/** `App.getInfo().build` 是字符串，安卓上是 versionCode；解析不出来就当读不到 */
export function parseVersionCode(build: string | undefined | null): number | null {
  if (!build) return null;
  const value = Number(build);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}
